import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextInput,
  Button,
  Text,
  Group,
  Stack,
  Divider,
  Alert,
  Collapse,
  UnstyledButton,
  Loader,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconMail, IconAlertCircle, IconLink, IconBrandWindows, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useAuthStore } from '../stores/auth';
import { getLoginOptions, requestMagicLink } from '../lib/api';
import styles from './LoginPage.module.css';

const MAGIC_ERROR_MESSAGES: Record<string, string> = {
  invalid: 'Der Anmeldelink ist ungültig oder wurde bereits verwendet.',
  expired: 'Der Anmeldelink ist abgelaufen. Bitte fordern Sie einen neuen an.',
  disabled: 'Dieses Konto ist deaktiviert.',
  wrong_browser:
    'Dieser Link kann nur im Browser geöffnet werden, in dem er angefordert wurde. Fordern Sie den Link dort erneut an und klicken Sie die E-Mail in demselben Browser.',
};

export function LoginPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();
  const [magicError, setMagicError] = useState('');
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [microsoftError, setMicrosoftError] = useState('');
  const [sessionEndedHint, setSessionEndedHint] = useState('');
  const [emergencyOpen, setEmergencyOpen] = useState(() => searchParams.get('tab') === 'magic');

  const { data: loginOptions, isLoading: loginOptionsLoading, isError: loginOptionsError } = useQuery({
    queryKey: ['auth', 'login-options'],
    queryFn: () => getLoginOptions(),
    staleTime: 60_000,
    retry: 2,
  });

  const ssoEnabled = loginOptions?.ssoEnabled ?? false;
  const localAdminEmail = loginOptions?.localAdminEmail ?? 'kie@fixbyte.it';

  useEffect(() => {
    const code = searchParams.get('magicError');
    if (!code) return;
    setEmergencyOpen(true);
    setMagicError(MAGIC_ERROR_MESSAGES[code] || MAGIC_ERROR_MESSAGES.invalid);
    const next = new URLSearchParams(searchParams);
    next.delete('magicError');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('tab') === 'magic') {
      setEmergencyOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const raw = searchParams.get('microsoftError');
    if (!raw) return;
    // Nachricht auf sichere Länge begrenzen und nur druckbare Zeichen erlauben
    const decoded = decodeURIComponent(raw.replace(/\+/g, ' '));
    const safe = decoded.replace(/[^\p{L}\p{N}\p{P}\p{Z}]/gu, '').slice(0, 300);
    setMicrosoftError(safe || 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
    const next = new URLSearchParams(searchParams);
    next.delete('microsoftError');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('reason') !== 'session_ended') return;
    setSessionEndedHint(
      'Ihre Sitzung wurde beendet — z. B. weil Sie sich woanders angemeldet haben, das Gerätelimit (3) erreicht ist oder eine andere Sitzung geschlossen wurde. Bitte melden Sie sich erneut an.'
    );
    const next = new URLSearchParams(searchParams);
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const magicForm = useForm({
    initialValues: { email: '' },
    validate: {
      email: (value) => (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) ? null : 'Ungültige E-Mail-Adresse'),
    },
  });

  useEffect(() => {
    if (import.meta.env.DEV) {
      magicForm.setFieldValue('email', localAdminEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAdminEmail]);

  const returnTo = searchParams.get('returnTo') ?? '';
  const safeReturnTo = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '';

  if (isAuthenticated) {
    return <Navigate to={safeReturnTo || '/'} replace />;
  }

  const handleMicrosoftLogin = () => {
    const url = safeReturnTo
      ? `/api/auth/sso/microsoft/authorize?returnTo=${encodeURIComponent(safeReturnTo)}`
      : '/api/auth/sso/microsoft/authorize';
    window.location.assign(url);
  };

  const handleMagicSubmit = async (values: typeof magicForm.values) => {
    const email = values.email.trim();
    magicForm.setFieldValue('email', email);

    setMagicError('');
    setMagicSent(false);
    setMagicLoading(true);

    try {
      const res = await requestMagicLink(email, safeReturnTo || undefined);

      if (import.meta.env.DEV && res.devMailStatus && res.devMailStatus !== 'sent') {
        setMagicSent(false);
        const hint =
          res.devMailStatus === 'skipped_not_local_admin'
            ? `Magic-Link ist hier nur für das Notfall-Konto (${localAdminEmail}) erlaubt — bitte genau diese Adresse verwenden.`
            : res.devMailStatus === 'skipped_no_user'
              ? 'In der Datenbank gibt es keinen Benutzer mit dieser E-Mail (leere DB oder Seed nicht gelaufen). Bitte Seed ausführen: npm run docker:db:seed oder npm run db:seed -w backend.'
              : 'Dieses Konto ist deaktiviert.';
        notifications.show({
          title: 'Anmeldelink',
          message: hint,
          color: 'orange',
          autoClose: 12_000,
        });
        return;
      }

      setMagicSent(true);
      setMagicError('');
      notifications.show({
        title: 'Anmeldelink angefordert',
        message:
          'Sofern ein Konto mit dieser E-Mail existiert, erhalten Sie in Kürze eine E-Mail mit dem Anmeldelink.',
        color: 'teal',
      });
    } catch (err) {
      setMagicSent(false);
      const msg = err instanceof Error ? err.message : 'Link konnte nicht angefordert werden';
      setMagicError(msg);
      notifications.show({
        title: 'Anmeldelink',
        message: msg,
        color: 'red',
      });
    } finally {
      setMagicLoading(false);
    }
  };

  const magicFormBlock = (
    <form onSubmit={magicForm.onSubmit(handleMagicSubmit)}>
      <Stack gap="md">
        <TextInput
          label="E-Mail"
          placeholder="name@unternehmen.de"
          leftSection={<IconMail size={18} />}
          required
          size="md"
          classNames={{ input: styles.input }}
          {...magicForm.getInputProps('email')}
        />
        <Button
          type="submit"
          loading={magicLoading}
          fullWidth
          size="md"
          className={styles.submitButton}
          variant="light"
          leftSection={<IconLink size={18} />}
        >
          Link per E-Mail senden
        </Button>
        {magicSent && (
          <Alert color="teal" variant="light" title="E-Mail unterwegs">
            Wenn ein Konto mit dieser E-Mail existiert, erhalten Sie in Kürze eine E-Mail mit dem Anmeldelink.
          </Alert>
        )}
      </Stack>
    </form>
  );

  return (
    <Box className={styles.container}>
      <div className={styles.backgroundPattern} />

      <Paper className={styles.loginCard} p="xl" radius="lg">
        <Stack gap="lg">
          <div className={styles.logoSection}>
            <img src="/fixbyte-logo-dark-bg.svg" alt="FixByte IT" style={{ height: 48, width: 'auto' }} />
          </div>

          <Divider label={<Text size="xs" c="dimmed">Techniker-Portal</Text>} labelPosition="center" />

          {loginOptionsLoading && (
            <Group justify="center" py="md">
              <Loader size="sm" />
            </Group>
          )}

          {sessionEndedHint && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="orange"
              variant="light"
              title="Sitzung beendet"
              onClose={() => setSessionEndedHint('')}
              withCloseButton
            >
              {sessionEndedHint}
            </Alert>
          )}

          {magicError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              title="Anmeldelink"
              onClose={() => setMagicError('')}
              withCloseButton
            >
              {magicError}
            </Alert>
          )}

          {microsoftError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              title="Microsoft-Anmeldung"
              onClose={() => setMicrosoftError('')}
              withCloseButton
            >
              {microsoftError}
            </Alert>
          )}

          {!loginOptionsLoading && loginOptionsError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="orange"
              variant="light"
              title="Portal vorübergehend nicht erreichbar"
            >
              Der Server antwortet gerade nicht. Bitte versuchen Sie es in einigen Minuten erneut oder wenden Sie sich an Ihre IT-Abteilung.
            </Alert>
          )}

          {!loginOptionsLoading && !loginOptionsError && ssoEnabled && (
            <>
              <Button
                variant="filled"
                color="gray"
                fullWidth
                size="md"
                leftSection={<IconBrandWindows size={18} />}
                onClick={handleMicrosoftLogin}
              >
                Mit Microsoft anmelden
              </Button>
              <Text size="xs" c="dimmed" ta="center">
                Empfohlene Anmeldung für alle Mitarbeitenden. Sie werden zu Microsoft weitergeleitet
                und danach wieder zum Portal (OAuth-Standard, kein Gerätecode).
              </Text>

              <Divider label="Alternative" labelPosition="center" />

              <UnstyledButton
                onClick={() => setEmergencyOpen((o) => !o)}
                style={{ width: '100%' }}
              >
                <Group justify="space-between" gap="xs" wrap="nowrap">
                  <Text size="sm" c="dimmed">
                    Anmeldung per E-Mail-Link
                  </Text>
                  {emergencyOpen ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
                </Group>
              </UnstyledButton>

              <Collapse expanded={emergencyOpen}>{magicFormBlock}</Collapse>
            </>
          )}

          {!loginOptionsLoading && !loginOptionsError && !ssoEnabled && (
            <Stack gap="lg">
              <Box className={styles.emailLoginIntro}>
                <Stack gap="sm" align="center">
                  <ThemeIcon size={48} radius="xl" variant="light" color="orange" className={styles.emailLoginIntroIcon}>
                    <IconMail size={26} stroke={1.5} />
                  </ThemeIcon>
                  <Title order={5} ta="center" fw={600} className={styles.emailLoginIntroTitle}>
                    Anmeldung per E-Mail
                  </Title>
                  <Text size="sm" ta="center" className={styles.emailLoginIntroLead}>
                    Tragen Sie Ihre geschäftliche E-Mail ein. Wir senden Ihnen einen Bestätigungslink.
                  </Text>
                  <Text size="xs" ta="center" c="dimmed">
                    Fragen zur Anmeldung? Ihre IT-Abteilung hilft gern weiter.
                  </Text>
                </Stack>
              </Box>
              {magicFormBlock}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Text className={styles.footer}>© 2026 FixByte IT • v{__APP_VERSION__}</Text>
    </Box>
  );
}
